// @flow

import React, {Component, Children, cloneElement} from 'react';
import {colors} from '@pb/kit-stylus/config.json';
import {NativeSortable, SORTABLE_DIRECTION, SORTABLE_POSITION} from '@pb/kit-component-native-sortable';
import {shouldUpdate} from '@pb/kit-util-should-update';
import {uniqId} from '@pb/kit-util-uniq-id';

import {DataTableCombinedContext} from './contexts/data-table-combined-context';
import {DataTablePlaceholderCell} from './data-table-placeholder-cell';
import {Cell} from './data-table-cell';
import {
    getScrollStep,
    createStyleVerticalLine
} from './utils';
import {ALLOWED_DRAG_AND_DROP_AREA_DATA_ID} from './contexts/data-table-drag-events-context';

import type {SortableChange, SortablePosition, ClientRectObject} from '@pb/kit-component-native-sortable';
import type {Element} from 'react';

import type {VirtualizeHorizontalData, TargetElementType, SortableItemEvent} from './typedef';
import type {DataTableCombinedContextType} from './contexts/data-table-combined-context';


type Props = {|
    onChange: (items: Array<string>, movableItemId: string, indexes: SortableItemEvent) => void,
    scroll: {|
        getScroll: () => ?HTMLElement,
        getScrollHandler: () => (delta: number) => void,
    |},
    disabled?: boolean,
    removable?: boolean,
    virtualizeHorizontalData: VirtualizeHorizontalData,
    children: Element<typeof Cell> | Array<Element<typeof Cell>>,
    getScrollableRef?: () => ?HTMLElement,
    tableId?: string,
|};

type State = {|
    targetElement: ?TargetElementType,
|};

const fallbackClass = 'data-table__native-sortable_fallback';
const dragBorderColor = colors.secondaryColors.orange;
const scrollInterval = 5;
const allowedDeviation = 45; // allowed deviation on column drag

export class DataTableReordering extends Component<Props, State> {
    scrollHandler: ?(delta: number) => void;
    style: ?HTMLStyleElement;
    key: string = uniqId('key');
    pinnedChildrenLength: number = 0;
    refScrollContainer: { current: HTMLElement | null } = {current: null};
    scrollRect: ?ClientRectObject = null;
    timerId: ?IntervalID = null;
    resizeObserver: ?ResizeObserver = null;

    static contextType: React$Context<DataTableCombinedContextType> = DataTableCombinedContext;

    static defaultProps = {
        disabled: false,
        removable: true
    };

    constructor(props: Props) {
        super(props);
        this.scrollHandler = null;
        this.pinnedChildrenLength = this.getPinnedChildren().length;
        this.state = {targetElement: null};
    }

    componentDidUpdate() {
        const {scrollContainerRef} = this.context.scrollContext;

        if (!this.refScrollContainer.current) {
            this.refScrollContainer = scrollContainerRef;
            this.updateScrollRect();
        }

        if (!this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(this.updateScrollRect);
        } else if (this.refScrollContainer.current) {
            this.resizeObserver.observe(this.refScrollContainer.current);
        }
    }

    componentWillUnmount() {
        if (this.refScrollContainer.current && this.resizeObserver) {
            this.resizeObserver.unobserve(this.refScrollContainer.current);
        }
    }

    updateScrollRect = () => {
        if (this.refScrollContainer.current) {
            const {top, right, bottom, left, width, height} =
                this.refScrollContainer.current.getBoundingClientRect();

            const scrollRect = {top: top - allowedDeviation, right, bottom, left, width, height};

            if (shouldUpdate(scrollRect, this.scrollRect)) {
                this.scrollRect = scrollRect;
            }
        }
    };

    removeStyles = () => {
        if (this.style && document.head) {
            document.head.removeChild(this.style);
            this.style = null;
        }
    };

    onTableBodyDrop = (e: SyntheticDragEvent<HTMLElement>, targetElement: ?TargetElementType) => {
        let {dataId, sortablePosition} = targetElement || {};

        if (dataId && dataId === ALLOWED_DRAG_AND_DROP_AREA_DATA_ID) {
            const allColumnIds = Children
                .toArray(this.props.children)
                .map(cell => cell.props.column.columnId);

            if (this.scrollRect && e.pageX > this.scrollRect.right) {
                dataId = allColumnIds[allColumnIds.length - 1];
                sortablePosition = SORTABLE_POSITION.AFTER;
            } else if (this.scrollRect && e.pageX < this.scrollRect?.left) {
                dataId = allColumnIds[0];
                sortablePosition = SORTABLE_POSITION.BEFORE;
                this.removeStyles();
            }
        }

        this.setState({targetElement: targetElement ? {dataId, sortablePosition} : null});
    };

    onTableBodyDrag = (e: SyntheticDragEvent<HTMLElement>, {dataId, sortablePosition}: TargetElementType) => {
        if (dataId === ALLOWED_DRAG_AND_DROP_AREA_DATA_ID) {

            if (this.scrollRect && e.pageX > this.scrollRect?.right) {
                const lastChildIndex = Children.toArray(this.props.children).length - 1;
                this.onHover(true, lastChildIndex, SORTABLE_POSITION.AFTER);
            } else if (this.scrollRect && e.pageX < this.scrollRect?.left) {
                const firstChildIndex = 0;
                this.onHover(true, firstChildIndex, SORTABLE_POSITION.BEFORE);
            }
        } else {
            Children.forEach(this.props.children, (child, index) => {
                if (child.key === dataId) {
                    this.onHover(true, index, sortablePosition);
                }
            });

            this.onDragOver(e);
        }
    };

    onChange = (data: SortableChange) => {
        const {draggable: {oldIndex, newIndex, id}, itemIds, type} = data;

        this.removeStyles();

        this.props.onChange(itemIds, id, {
            oldIndex: this.validateColumnIndex(oldIndex),
            newIndex: this.validateColumnIndex(newIndex),
            type
        });

        if (this.timerId) {
            clearInterval(this.timerId);
            this.timerId = null;
        }

        this.key = uniqId('key');

        if (this.state.targetElement) {
            this.setState({targetElement: null});
        }
    };

    validateColumnIndex = (index: number): number => {
        const {cellStartIndex} = this.props.virtualizeHorizontalData;

        return this.pinnedChildrenLength <= cellStartIndex ?
            cellStartIndex + index : this.pinnedChildrenLength + index;
    };

    getItems() {
        const {children, disabled} = this.props;
        return Children.map(children, child => {
            if (child.props.column?.pinned || disabled) {
                return child;
            }
            return cloneElement(child, {sortableCell: true});
        });
    }

    onScroll = (delta: number) => {
        if (delta) {
            if (!this.scrollHandler) {
                this.scrollHandler = this.props.scroll.getScrollHandler();
            }
            this.scrollHandler(delta);
        }
    };

    onDragOver = (e: SyntheticDragEvent<HTMLElement>) => {
        if (this.scrollRect) {
            const scrollStep = getScrollStep(this.scrollRect, {x: e.pageX, y: e.pageY});

            if (scrollStep && !this.timerId) {
                this.setScrollPosition(scrollStep);
            }

            if (!scrollStep || scrollStep < 0) {
                clearInterval(this.timerId);
                this.timerId = null;
            }
        }
    };

    onDragLeave = () => {
        clearInterval(this.timerId);
        this.timerId = null;
    };

    onDrop = () => {
        this.context.dataTableDragEventsContext.resetOnDropSubscriber();
        this.context.dataTableDragEventsContext.resetOnDragSubscriber();
    };

    onDragStart = (e: SyntheticDragEvent<HTMLElement>) => {
        this.context.dataTableDragEventsContext.setOnDropSubscriber(this.onTableBodyDrop);
        this.context.dataTableDragEventsContext.setOnDragSubscriber(this.onTableBodyDrag);
        this.context.dataTableDragEventsContext.onDragStart(e);
    };

    onDragEnd = (e: SyntheticDragEvent<HTMLElement>) => {
        this.context.dataTableDragEventsContext.onDragEnd(e);
    };

    setScrollPosition = (scrollStep: number) => {
        const scrollContainer = this.refScrollContainer.current;

        if (scrollContainer) {
            this.timerId = setInterval(() => {
                scrollContainer.scrollLeft += scrollStep;
            }, scrollInterval);
        }
    };

    getPinnedChildren = (): Array<React$Element<any>> => {
        return Children.toArray(this.props.children).filter(({props: {column: {pinned} = {}}}) => pinned);
    };

    onHover = (shown: boolean, insertIndex: number, position: SortablePosition) => {
        this.removeStyles();

        if (shown) {
            const style = createStyleVerticalLine({
                position,
                columnIndex: this.validateColumnIndex(insertIndex),
                colorLine: dragBorderColor,
                tableId: this.props.tableId || '',
            });

            if (document.head) {
                this.style = style;
                document.head.appendChild(this.style);
            }
        }
    };

    render() {
        const {disabled, removable, virtualizeHorizontalData} = this.props;
        const {targetElement} = this.state;
        const {isVirtualizedCells, rightOffset, leftOffset} = virtualizeHorizontalData;

        return (
            <NativeSortable
                dataIdAttr="id"
                direction={SORTABLE_DIRECTION.HORIZONTAL}
                mix={'data-table__native-sortable'}
                ghostMix={fallbackClass}
                disabled={disabled}
                removable={removable}
                onApply={this.onChange}
                onDrop={this.onDrop}
                onDragStart={this.onDragStart}
                onDragEnd={this.onDragEnd}
                onDragOver={this.onDragOver}
                onDragLeave={this.onDragLeave}
                onHover={this.onHover}
                allowedDragArea={this.scrollRect}
                targetElement={targetElement}>
                <>
                    <DataTablePlaceholderCell width={leftOffset} isVirtualizedCells={isVirtualizedCells}/>
                    {this.getItems()}
                    <DataTablePlaceholderCell width={rightOffset} isVirtualizedCells={isVirtualizedCells}/>
                </>
             </NativeSortable>
        );
    }
}

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./typedef");

require("./data-table-cell");

require("./contexts/data-table-combined-context");
